import React from "react"
import { Link } from "gatsby"
import styled from "styled-components"
import colors from "@constants/colors"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import {
  body16MainStyles,
  smallcapsRegular13Styles,
} from "@constants/typography"
import AnimatedDash, {
  animationStyles,
} from "@components/buttons/common/AnimatedDash"
import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import ProductTag, {
  Container as ProductTagContainer,
} from "@components/ProductTag"
import { getProductURL } from "@utils/urls"
import { TabletAndDesktopOnly } from "@utils/MediaQuery"
import _ from "lodash-es"

const GatsbyLink = styled(Link)<any>`
  ${({ to }) => (!to.length ? "pointer-events: none" : "cursor: pointer")};
  ${animationStyles}
`

const Container = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  position: relative;

  ${ProductTagContainer} {
    height: 32px;
    margin-left: 5.84px;
    margin-bottom: 4.32px;
  }
`

const Content = styled.div`
  width: 100%;
  padding-top: 14px;
`

const Row = styled.div`
  display: flex;
  margin-bottom: 4px;
  align-items: center;
  justify-content: space-between;
`

const ImageWrapper = styled.div`
  position: relative;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-end;
  display: flex;
  padding: 16px;
  transition: background-color ${animation.medium} ${cubicBezier};
  background-color: ${colors.backgroundGray};

  @media ${breakpoints.laptop} {
    align-self: stretch;
    justify-self: stretch;
    padding: 20px;

    &:hover {
      background-color: ${colors.backgroundGrayHover};
    }
  }

  ${ImageHelperContainer} {
    flex-grow: 0;
    flex-shrink: 0;
    width: 100%;
    height: auto;

    img {
      object-fit: contain;
    }
  }
`

const Title = styled.div`
  ${body16MainStyles}
`

const Price = styled.div`
  ${smallcapsRegular13Styles}
  opacity: 0.3;
`

const DashWrapper = styled.div`
  margin-right: 16px;
`

const Tags = styled.div`
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  display: flex;
  margin-top: auto;
`

const UByUAGTagWrapper = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  height: 32px;
  margin-bottom: 2px;
  padding-left: 7.5px;
  background-color: ${colors.brandMagenta};
  overflow: hidden;
`

export interface SearchBlockProps {
  slug: string
  name: string
  price: string
  imageUrl: string
  tags?: any[]
  onClick?: (e: any) => void
}

const SearchBlock: React.FC<SearchBlockProps> = ({
  slug,
  name,
  price,
  imageUrl,
  onClick,
  tags,
}: SearchBlockProps) => {
  const [uByUagTag, otherTags] = _.partition(
    tags,
    (tag) => tag?.text === "U by UAG"
  )

  return (
    <GatsbyLink to={getProductURL(slug)} onClick={onClick}>
      <Container>
        <ImageWrapper>
          {(uByUagTag[0] || otherTags.length > 0) && (
            <Tags>
              {uByUagTag[0] && (
                <UByUAGTagWrapper>
                  <svg
                    width="14"
                    height="12"
                    viewBox="0 0 14 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect width="14" height="12" fill="#FFFEFD" />
                    <rect
                      x="2.26953"
                      y="1.66406"
                      width="2.20721"
                      height="8.61244"
                      fill="#9C3769"
                    />
                    <rect
                      x="9.52148"
                      y="1.66406"
                      width="2.20721"
                      height="8.61244"
                      fill="#9C3769"
                    />
                    <rect
                      x="2.26953"
                      y="10.2773"
                      width="2.00957"
                      height="9.45946"
                      transform="rotate(-90 2.26953 10.2773)"
                      fill="#9C3769"
                    />
                  </svg>
                  <ProductTag
                    key="uByUAGProduct"
                    text="U by UAG"
                    backgroundColor={colors.brandMagenta.substr(1)}
                  />
                </UByUAGTagWrapper>
              )}
              {otherTags &&
                otherTags.length > 0 &&
                otherTags.map((tag) => <ProductTag key={tag.text} {...tag} />)}
            </Tags>
          )}
          <ImageHelper src={imageUrl} />
        </ImageWrapper>
        <Content>
          <Row>
            <Title>{name}</Title>
            <TabletAndDesktopOnly>
              <DashWrapper>
                <AnimatedDash />
              </DashWrapper>
            </TabletAndDesktopOnly>
          </Row>
          <Price>{price}</Price>
        </Content>
      </Container>
    </GatsbyLink>
  )
}

export default SearchBlock
